import { useMemo, useState } from "react";

import isNil from "lodash/isNil";

import { SMTAnalyticsSource } from "@/constants/analytics/Analytics";
import { ModalTestID, NavTestID } from "@/constants/TestID";
import { router } from "@/crossPlatform/expo-router";
import { useAnalytics } from "@/services/Analytics";
import { useAppUserSelector } from "@/store/reducers/appUser";
import type { SMTSingleDayEntriesAccordionProps } from "@/ui/components/smt/ViewSMTEntries/SMTSingleDayEntriesAccordion";
import SMTSingleDayEntriesAccordion from "@/ui/components/smt/ViewSMTEntries/SMTSingleDayEntriesAccordion";
import ModalTitleHeader from "@/ui/composed/modal/ModalTitleHeader";
import Spinner from "@/ui/core/Spinner";
import type { UserTabSelectorTabProps } from "@/ui/core/UserTabSelector";
import UserTabSelector from "@/ui/core/UserTabSelector";
import type { FlatListProps } from "@/ui/layout";
import { FlatList, VStack } from "@/ui/layout";
import ModalLayout from "@/ui/layout/ModalLayout";
import { isNotNilNotEmpty } from "@/utils/shared.utils";
import { joinTestIDs } from "@/utils/string.util";

type AccordionPropsListType = Omit<
  SMTSingleDayEntriesAccordionProps,
  "analyticsSource" | "isOpen" | "onTriggerPress" | "testID"
>;

interface AllSMTDailyEntriesModalProps extends ViewWithTestID {
  accordionPropsList: AccordionPropsListType[];
  analyticsSource?: ValueOf<typeof SMTAnalyticsSource>;
  isFetchingNextPage?: boolean;
  isRefreshing?: boolean;
  onEndReached?: FlatListProps<AccordionPropsListType>["onEndReached"];
  onEndReachedThreshold?: number;
  onRefresh?: () => void;
  onUserChange?: (userId: string) => void;
  userTabs?: UserTabSelectorTabProps[];
}

const FlatListSeparatorPadding = 18;

// only trigger onEndReached/fetch more pages once UI is 10% away from the end of the list
// to not trigger too many API requests right off the bat when modal loads
const OnEndReachedThreshold = 0.1;

const AllSMTDailyEntriesModal = ({
  accordionPropsList,
  analyticsSource = SMTAnalyticsSource.ViewAllSMTDailyEntriesModal,
  isFetchingNextPage,
  isRefreshing,
  onEndReached,
  onEndReachedThreshold = OnEndReachedThreshold,
  onRefresh,
  onUserChange,
  testID,
  userTabs,
}: AllSMTDailyEntriesModalProps) => {
  const { externalId: appUserId } = useAppUserSelector();
  const { trackUserSelectorEvent } = useAnalytics();

  const headerActionComponent = useMemo(() => {
    if (!isNotNilNotEmpty(userTabs)) return null;

    return (
      <UserTabSelector
        accessibilityLabel="User Tab Selector"
        appUserId={appUserId}
        onUserChange={(userId: string) => {
          trackUserSelectorEvent({
            component: AllSMTDailyEntriesModal.name,
            selectedUser: userId,
            source: analyticsSource,
          });
          onUserChange?.(userId);
        }}
        tabs={userTabs}
        testID={joinTestIDs(
          testID,
          ModalTestID.TitleHeader,
          NavTestID.TabSelector,
        )}
      />
    );
  }, [
    analyticsSource,
    appUserId,
    onUserChange,
    testID,
    trackUserSelectorEvent,
    userTabs,
  ]);

  // Find the first index that has entries (most recent day with entries)
  const firstIndexWithEntries = useMemo(() => {
    return accordionPropsList.findIndex((item) => item.entries.length > 0);
  }, [accordionPropsList]);

  // open entry state is managed here, rather than per-accordion, due to FlatList's use of FlashList
  // which keeps the same component instances live, and changes their props on scroll,
  // which means any internally-managed React state (ie, useState) would need extra complexity there to manage
  const [openEntries, setOpenEntries] = useState<Record<string, boolean>>({});

  const ListFooterComponent = useMemo(() => {
    if (!isFetchingNextPage) return null;

    const basePadding = FlatListSeparatorPadding;
    return (
      <VStack
        paddingBottom={2 * basePadding}
        paddingTop={basePadding}
        testID={joinTestIDs(testID, "spinner")}
      >
        <Spinner />
      </VStack>
    );
  }, [isFetchingNextPage, testID]);

  return (
    <ModalLayout
      isListView
      renderHeader={() => (
        <ModalTitleHeader
          actionComponents={headerActionComponent}
          onDismiss={() => router.dismiss()}
          testID={joinTestIDs(testID, ModalTestID.TitleHeader)}
          title="All entries"
        />
      )}
    >
      <FlatList
        data={accordionPropsList}
        isRefreshControlLoading={isRefreshing}
        isScrollEnabled
        keyExtractor={(item) => item.dailyLogId}
        ListFooterComponent={ListFooterComponent}
        onEndReached={onEndReached}
        onEndReachedThreshold={onEndReachedThreshold}
        onRefresh={onRefresh}
        renderItem={({ index, item }) => {
          const { dailyLogId, date, entries } = item;
          const isOpen =
            openEntries[dailyLogId] ?? index === firstIndexWithEntries;
          return (
            <SMTSingleDayEntriesAccordion
              analyticsSource={analyticsSource}
              dailyLogId={dailyLogId}
              date={date}
              entries={entries}
              isOpen={isOpen}
              onTriggerPress={(isNextOpen) =>
                setOpenEntries((prev) => ({
                  ...prev,
                  [dailyLogId]: isNextOpen,
                }))
              }
              testID={testID}
            />
          );
        }}
        separatorPadding={FlatListSeparatorPadding}
        shouldDisableMaintainVisibleContentPosition
        shouldShowRefreshControl={!isNil(onRefresh)}
        testID={testID}
      />
    </ModalLayout>
  );
};

export default AllSMTDailyEntriesModal;
export type { AllSMTDailyEntriesModalProps };
